import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isSmoltLocation } from '@/utils/locationTypeHelpers';

import { penLabelKey } from './penLabel';
import type {
    LocationOverviewColumnContext,
    LocationOverviewColumnKey,
    LocationOverviewColumnVisibility,
} from './types';

/** The "Open" button column needs no more room than the button itself. */
const CAGE_COLUMN_WIDTH = '120px';

/**
 * Wider, and elastic, for when the button carries the pen name instead of "Open".
 * Folds in the track the `name` column gave up, so the row keeps its proportions.
 */
const CAGE_NAME_COLUMN_WIDTH = 'minmax(120px, 0.8fr)';

/** A visible column: its key, header text and grid track. */
export interface LocationOverviewColumn {
    key: LocationOverviewColumnKey;
    header: string;
    width: string;
}

interface LocationOverviewColumns {
    /** Header cells and grid tracks, already filtered down to what is visible. */
    columns: LocationOverviewColumn[];
    /** Which body cells each row should emit, resolved from the same predicates. */
    visibility: LocationOverviewColumnVisibility;
}

/**
 * Single source of truth for the column set: header and body cells are derived
 * from the same predicates, so they can never drift out of sync.
 */
export function useLocationOverviewColumns(
    context: LocationOverviewColumnContext
): LocationOverviewColumns {
    const {
        locationType,
        hasAccessToLice,
        hasAccessToFishSpeed,
        hasAccessToBehavior,
        showBetaLaksvelColumn,
        showWoundColumn,
        showMarkHistorical,
        showFishGroupColumn,
    } = context;
    const { t } = useTranslation();
    const { t: tHealth } = useTranslation('healthModule');
    const { t: tFishGroups } = useTranslation('fishGroups');

    const isSmolt = isSmoltLocation(locationType);

    const visibility = useMemo<LocationOverviewColumnVisibility>(
        () => ({
            cage: true,
            // Dropped once the cohort has a column of its own: the button then
            // carries the pen name, and the name belongs in exactly one place.
            name: !showFishGroupColumn,
            fishGroup: showFishGroupColumn,
            weight: true,
            growth: true,
            lice: hasAccessToLice && !isSmolt,
            laksvel: showBetaLaksvelColumn,
            wound: showWoundColumn,
            speed: (hasAccessToFishSpeed || hasAccessToBehavior) && !isSmolt,
            measurement: true,
            historical: showMarkHistorical,
        }),
        [
            hasAccessToLice,
            hasAccessToFishSpeed,
            hasAccessToBehavior,
            showBetaLaksvelColumn,
            showWoundColumn,
            showMarkHistorical,
            showFishGroupColumn,
            isSmolt,
        ]
    );

    const columns = useMemo<LocationOverviewColumn[]>(() => {
        const allColumns: LocationOverviewColumn[] = [
            // Headerless while it is only an "Open" button; once it carries the
            // pen name it is a real column and says what those names are.
            {
                key: 'cage',
                header: showFishGroupColumn ? t(penLabelKey(locationType)) : '',
                width: showFishGroupColumn ? CAGE_NAME_COLUMN_WIDTH : CAGE_COLUMN_WIDTH,
            },
            { key: 'name', header: t('common.labels.name'), width: 'minmax(80px, 0.8fr)' },
            // Sits next to the pen name on purpose: it says what is *in* the pen,
            // so the two read together before the metrics start.
            {
                key: 'fishGroup',
                header: tFishGroups('locationOverview.columnHeader'),
                width: 'minmax(110px, 1.2fr)',
            },
            { key: 'weight', header: t('common.labels.weight'), width: 'minmax(60px, 0.8fr)' },
            { key: 'growth', header: t('common.labels.growth'), width: 'minmax(75px, 0.8fr)' },
            { key: 'lice', header: t('common.labels.lice'), width: 'minmax(75px, 0.8fr)' },
            {
                key: 'laksvel',
                header: tHealth('locationOverview.laksvelColumnHeader'),
                width: 'minmax(90px, 1fr)',
            },
            { key: 'wound', header: t('common.labels.wound'), width: 'minmax(75px, 0.8fr)' },
            { key: 'speed', header: t('common.labels.swimSpeed'), width: 'minmax(82px, 0.8fr)' },
            {
                key: 'measurement',
                header: t('common.labels.lastMeasurement'),
                width: 'minmax(125px, 1fr)',
            },
            { key: 'historical', header: t('common.labels.historical'), width: '72px' },
        ];

        return allColumns.filter((column) => visibility[column.key]);
    }, [t, tHealth, tFishGroups, visibility, showFishGroupColumn, locationType]);

    return { columns, visibility };
}
